import React, { useState } from 'react';
import { IInfoPopup, InfoPopup, useInfoPopup } from './InfoPopup';
import { alpha, Box, Theme } from '@mui/material';
import { Stack } from '@mui/system';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';

interface IInfoStatus {
    info?: string[];
    warning?: string[];
    error?: string[];
    success?: string[];
}
interface IInfoStatusPopup extends Omit<IInfoPopup, 'content, setContent'> {
    status: IInfoStatus;
    setStatus: (value: IInfoStatus) => void;
}

export function useInfoStatusPopup(onHide?: () => void, defaultStatus?: IInfoStatus): IInfoStatusPopup {
    const result = useInfoPopup(onHide);
    const [status, setStatus] = useState(defaultStatus ?? {});
    return {
        ...result,
        status,
        setStatus,
    };
}

function PopupContent({ status }: { status: IInfoStatus }) {
    const { t } = useBundleTranslation();

    const typesMap: Record<
        keyof IInfoStatus,
        {
            icon: string;
            title: string;
        }
    > = {
        success: {
            icon: 'success',
            title: '',
        },
        error: {
            icon: 'alert',
            title: 'error',
        },
        warning: {
            icon: 'info-circle',
            title: 'warning',
        },
        info: {
            icon: 'info-circle',
            title: 'info',
        },
    };

    return (
        <Box
            sx={{
                py: 1,
                '.test:not(:first-child)': {
                    pt: 2,
                    mt: 2,
                    borderTop: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                },
            }}
        >
            {(Object.keys(typesMap) as (keyof IInfoStatus)[])
                .map((type) => {
                    if (!status[type]?.length) {
                        return null;
                    }
                    return (
                        <Stack
                            key={type}
                            className={'test'}
                            direction="row"
                            sx={{ maxWidth: '360px', wordBreak: 'break-word' }}
                        >
                            <Box>
                                <IconMi
                                    sx={{
                                        fontSize: '16px',
                                        mr: 1,
                                        color: (theme: Theme) =>
                                            type == 'info' ? undefined : theme.palette[type].main,
                                    }}
                                    icon={typesMap[type].icon}
                                />
                            </Box>
                            <Box>
                                {status[type].map((line, index) => (
                                    <Box key={index} sx={{ mt: index > 0 ? 0.5 : 0 }}>
                                        {index === 0 && typesMap[type].title ? (
                                            <span style={{ textTransform: 'uppercase' }}>{t(type)}: </span>
                                        ) : (
                                            ''
                                        )}
                                        {line}
                                    </Box>
                                ))}
                            </Box>
                        </Stack>
                    );
                })
                .filter((r) => r !== null)}
        </Box>
    );
}
export function InfoStatusPopupWrapper({ infoStatusPopup }: { infoStatusPopup: IInfoStatusPopup }) {
    const handleOnHide = () => {
        infoStatusPopup.setIsOpen(false);
        if (infoStatusPopup.onHide) {
            infoStatusPopup.onHide();
        }
    };

    return (
        <InfoPopup
            maxWidth="popupSm"
            title={infoStatusPopup.title}
            onHide={handleOnHide}
            isOpen={infoStatusPopup.isOpen}
        >
            <PopupContent status={infoStatusPopup.status} />
        </InfoPopup>
    );
}
